import { request } from "@data/datoRequest"
import { allUpholsteryStepsV2Query } from "@views/Upholstery/graphql"
import type {
  Category,
  Compatibility,
  OptionValue,
  Step,
  Substep,
} from "@views/Upholstery/types"
import { useUpholsteryStepsV2Store } from "../../store/upholstery-steps-v2-store"

/** Style steps owned by the global Upholstery Steps V2 collection. */
export const GLOBAL_STYLE_STEP_KEYS = [
  "build",
  "kit",
  "pattern",
  "design",
  "material",
] as const

export type GlobalStyleStepKey = (typeof GLOBAL_STYLE_STEP_KEYS)[number]

const GLOBAL_STYLE_STEP_KEY_SET = new Set<string>(GLOBAL_STYLE_STEP_KEYS)

export function isGlobalStyleStepKey(key: string): key is GlobalStyleStepKey {
  return GLOBAL_STYLE_STEP_KEY_SET.has(key)
}

/**
 * Drop Build / Kit / Pattern / Design / Material from a car's steps.
 * Used when Steps V2 is on so leftover nested style data from the per-car
 * query is never treated as a source of truth.
 */
export function withoutGlobalStyleSteps(steps: Step[]): Step[] {
  return steps.filter((step) => !isGlobalStyleStepKey(step.key))
}

type RawCompatibility = {
  key?: string | null
  values?: string | null
}

type RawOptionValue = {
  id?: string | null
  optionName?: string | null
  sku?: string | null
  optionValue?: string | null
  slug?: string | null
  optionImage?: OptionValue["optionImage"]
  compatibility?: RawCompatibility[] | null
  twoRowPrice?: string | number | null
  threeRowPrice?: string | number | null
}

type RawCategory = {
  optionName?: string | null
  optionValues?: RawOptionValue[] | null
}

type RawNestedSubstep = {
  id?: string | null
  key?: string | null
  name?: string | null
  value?: string | null
  category?: RawCategory | null
}

type RawSubstep = {
  id?: string | null
  key?: string | null
  name?: string | null
  value?: string | null
  /** Dato JSON field "Build Options" — e.g. `{ twoRowPrice, threeRowPrice }`. */
  buildOptions?: unknown
  category?: RawCategory | null
  substeps?: RawNestedSubstep[] | null
}

type RawUpholsteryStepsV2Record = {
  id?: string | null
  key?: string | null
  name?: string | null
  description?: string | null
  category?: RawCategory | null
  steps?: RawSubstep[] | null
}

function parsePrice(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === "") {
    return null
  }
  const n = typeof value === "number" ? value : Number(value)
  return Number.isFinite(n) ? n : null
}

/**
 * Parse Dato "Build Options" JSON (object or string) into row prices.
 * Expected shape: `{ "twoRowPrice": 1595, "threeRowPrice": 1895 }`.
 */
function parseBuildOptionsPrices(raw: unknown): {
  twoRowPrice: number | null
  threeRowPrice: number | null
} {
  let obj: Record<string, unknown> | null = null
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw) as unknown
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        obj = parsed as Record<string, unknown>
      }
    } catch {
      return { twoRowPrice: null, threeRowPrice: null }
    }
  } else if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    obj = raw as Record<string, unknown>
  }

  if (!obj) {
    return { twoRowPrice: null, threeRowPrice: null }
  }

  return {
    twoRowPrice: parsePrice(obj.twoRowPrice as string | number | null),
    threeRowPrice: parsePrice(obj.threeRowPrice as string | number | null),
  }
}

function mapCompatibility(
  rows: RawCompatibility[] | null | undefined
): Compatibility[] {
  if (!Array.isArray(rows)) return []
  return rows
    .filter((row): row is RawCompatibility => !!row && typeof row === "object")
    .map((row) => ({
      key: row.key ?? "",
      values: row.values ?? "",
    }))
}

function mapOptionValue(raw: RawOptionValue): OptionValue {
  return {
    id: raw.id ?? "",
    optionName: raw.optionName ?? "",
    sku: raw.sku ?? "",
    optionValue: raw.optionValue ?? "",
    slug: raw.slug ?? "",
    optionImage: raw.optionImage ?? null,
    compatibility: mapCompatibility(raw.compatibility),
    twoRowPrice: parsePrice(raw.twoRowPrice),
    threeRowPrice: parsePrice(raw.threeRowPrice),
  }
}

function mapCategory(raw: RawCategory | null | undefined): Category {
  return {
    optionName: raw?.optionName ?? "",
    optionValues: Array.isArray(raw?.optionValues)
      ? raw!.optionValues.map(mapOptionValue)
      : [],
  }
}

function mapNestedSubstep(raw: RawNestedSubstep): Substep {
  return {
    id: raw.id ?? "",
    key: raw.key ?? "",
    title: raw.name ?? "",
    defaultValue: raw.value ?? "",
    category: mapCategory(raw.category),
    factoryMatches: [],
    colorInsertMatches: [],
    substeps: [],
  }
}

function mapSubstep(raw: RawSubstep): Substep {
  return {
    id: raw.id ?? "",
    key: raw.key ?? "",
    title: raw.name ?? "",
    defaultValue: raw.value ?? "",
    category: mapCategory(raw.category),
    factoryMatches: [],
    colorInsertMatches: [],
    substeps: Array.isArray(raw.substeps)
      ? raw.substeps.map(mapNestedSubstep)
      : [],
  }
}

/**
 * Map one Upholstery Steps V2 record into the app `Step` shape.
 * Build is special: custom/factory live as child `steps` in CMS, but the UI
 * expects flat `category.optionValues`.
 */
export function mapUpholsteryStepsV2RecordToStep(
  raw: RawUpholsteryStepsV2Record
): Step {
  const key = (raw.key ?? "").trim()
  const title = raw.name ?? key

  if (key === "build") {
    const parentOptions = raw.category?.optionValues ?? []
    const buildOptions: OptionValue[] = (raw.steps ?? []).map((child) => {
      const optionValue = (child.value || child.key || "").trim()
      const nested = child.category?.optionValues?.[0]
      const fromParent = parentOptions.find(
        (ov) => (ov.optionValue ?? "").trim() === optionValue
      )
      const fromBuildOptions = parseBuildOptionsPrices(child.buildOptions)
      return {
        id: child.id ?? "",
        optionName: child.name ?? "",
        optionValue,
        sku: nested?.sku ?? fromParent?.sku ?? "",
        optionImage: nested?.optionImage ?? null,
        compatibility: mapCompatibility(
          nested?.compatibility ?? fromParent?.compatibility
        ),
        twoRowPrice:
          fromBuildOptions.twoRowPrice ??
          parsePrice(nested?.twoRowPrice) ??
          parsePrice(fromParent?.twoRowPrice),
        threeRowPrice:
          fromBuildOptions.threeRowPrice ??
          parsePrice(nested?.threeRowPrice) ??
          parsePrice(fromParent?.threeRowPrice),
      }
    })

    return {
      id: raw.id ?? "",
      key: "build",
      title,
      compatibility: null,
      category: {
        optionName: raw.description || "Seat Build",
        optionValues: buildOptions,
      },
      substeps: [],
      factoryMatches: [],
    }
  }

  return {
    id: raw.id ?? "",
    key,
    title,
    compatibility: null,
    category: mapCategory(raw.category),
    substeps: Array.isArray(raw.steps) ? raw.steps.map(mapSubstep) : [],
    factoryMatches: [],
  }
}

function hasRowPrice(opt: OptionValue): boolean {
  const two = Number(opt.twoRowPrice)
  const three = Number(opt.threeRowPrice)
  return (
    (Number.isFinite(two) && two > 0) || (Number.isFinite(three) && three > 0)
  )
}

/**
 * When Steps V2 replaces per-car Build, apply per-car Build prices when present.
 * Per-car prices win over global Build Options JSON defaults.
 * If the car has no Build prices, keep the global defaults (JSON / kit fallback).
 */
export function overlayMissingBuildPricesFromCar(
  steps: Step[],
  carSteps: Step[]
): Step[] {
  const globalBuild = steps.find((step) => step.key === "build")
  const carBuild = carSteps.find((step) => step.key === "build")
  const carOptions = carBuild?.category?.optionValues ?? []
  if (!globalBuild || carOptions.length === 0) {
    return steps
  }

  const globalOptions = globalBuild.category?.optionValues ?? []
  if (globalOptions.length === 0) {
    return steps
  }

  let changed = false
  const mergedOptions = globalOptions.map((opt) => {
    const carOpt = carOptions.find(
      (candidate) =>
        candidate.optionValue.trim().toLowerCase() ===
        opt.optionValue.trim().toLowerCase()
    )
    // No per-car prices → keep global Build Options JSON (or empty).
    if (!carOpt || !hasRowPrice(carOpt)) return opt

    const twoRowPrice =
      parsePrice(carOpt.twoRowPrice as string | number | null) ??
      opt.twoRowPrice ??
      null
    const threeRowPrice =
      parsePrice(carOpt.threeRowPrice as string | number | null) ??
      opt.threeRowPrice ??
      null

    changed = true
    return {
      ...opt,
      twoRowPrice,
      threeRowPrice,
      sku: carOpt.sku || opt.sku || "",
    }
  })

  if (!changed) return steps

  return steps.map((step) =>
    step.key === "build"
      ? {
          ...step,
          category: {
            ...step.category,
            optionName: step.category?.optionName ?? "Seat Build",
            optionValues: mergedOptions,
          },
        }
      : step
  )
}

/**
 * PDM-only cars have no per-car Dato Build prices. Factory Match always uses
 * kit code XS (Standard), so copy Standard kit row prices onto the factory
 * option when it is still empty.
 */
export function overlayMissingFactoryPricesFromKit(steps: Step[]): Step[] {
  const build = steps.find((step) => step.key === "build")
  const kit = steps.find((step) => step.key === "kit")
  const factoryOpt = build?.category?.optionValues?.find(
    (opt) => opt.optionValue.trim().toLowerCase() === "factory"
  )
  if (!build || !factoryOpt || hasRowPrice(factoryOpt)) {
    return steps
  }

  const kitStandard = kit?.category?.optionValues?.find((opt) => {
    const value = opt.optionValue.trim().toLowerCase()
    const sku = (opt.sku ?? "").trim().toUpperCase()
    return value === "standard" || sku === "XS"
  })
  if (!kitStandard || !hasRowPrice(kitStandard)) {
    return steps
  }

  const twoRowPrice =
    parsePrice(kitStandard.twoRowPrice as string | number | null) ?? null
  const threeRowPrice =
    parsePrice(kitStandard.threeRowPrice as string | number | null) ?? null

  return steps.map((step) => {
    if (step.key !== "build") return step
    return {
      ...step,
      category: {
        ...step.category,
        optionName: step.category?.optionName ?? "Seat Build",
        optionValues: (step.category?.optionValues ?? []).map((opt) => {
          if (opt.optionValue.trim().toLowerCase() !== "factory") return opt
          if (hasRowPrice(opt)) return opt
          return { ...opt, twoRowPrice, threeRowPrice }
        }),
      },
    }
  })
}

/**
 * Replace per-car style steps with global V2 records.
 * Inserts any missing global steps just before `factory-match` when present.
 */
export function spliceGlobalStyleSteps(
  carSteps: Step[],
  globalStyleSteps: Step[]
): Step[] {
  const globalByKey = new Map(globalStyleSteps.map((step) => [step.key, step]))
  const used = new Set<string>()
  const result: Step[] = []

  for (const step of carSteps) {
    const replacement = globalByKey.get(step.key)
    if (replacement) {
      result.push(replacement)
      used.add(step.key)
    } else {
      result.push(step)
    }
  }

  const missing = globalStyleSteps.filter((step) => !used.has(step.key))
  if (missing.length === 0) {
    return result
  }

  const factoryMatchIndex = result.findIndex(
    (step) => step.key === "factory-match"
  )
  if (factoryMatchIndex >= 0) {
    result.splice(factoryMatchIndex, 0, ...missing)
  } else {
    result.push(...missing)
  }

  return result
}

/**
 * Fetch global Build / Kit / Pattern / Design / Material from Dato.
 * Stores the result in `useUpholsteryStepsV2Store` and reuses it for later calls.
 */
let stepsV2Inflight: Promise<Step[]> | null = null

export async function fetchUpholsteryStepsV2(): Promise<Step[]> {
  const cached = useUpholsteryStepsV2Store.getState().steps
  if (cached.length > 0) {
    return cached
  }

  if (stepsV2Inflight) {
    return stepsV2Inflight
  }

  const { actions } = useUpholsteryStepsV2Store.getState()
  actions.setLoading(true)

  stepsV2Inflight = (async () => {
    try {
      const response = await request({
        query: allUpholsteryStepsV2Query,
      })

      const records = (response?.allUpholsterCarsSteps ??
        []) as RawUpholsteryStepsV2Record[]

      const mapped = records
        .map(mapUpholsteryStepsV2RecordToStep)
        .filter((step) => isGlobalStyleStepKey(step.key))

      const byKey = new Map(mapped.map((step) => [step.key, step]))
      const ordered: Step[] = []
      const missing: string[] = []

      for (const key of GLOBAL_STYLE_STEP_KEYS) {
        const step = byKey.get(key)
        if (!step) {
          missing.push(key)
          continue
        }
        ordered.push(step)
      }

      if (missing.length > 0) {
        throw new Error(
          `[Upholstery Steps V2] Missing required global steps: ${missing.join(
            ", "
          )}. Expected keys: ${GLOBAL_STYLE_STEP_KEYS.join(", ")}.`
        )
      }

      actions.setSteps(ordered)
      return ordered
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "[Upholstery Steps V2] Failed to fetch global steps."
      actions.setError(message)
      throw error
    } finally {
      stepsV2Inflight = null
    }
  })()

  return stepsV2Inflight
}
